import React from 'react';
import { Building2, ArrowRight, Eye, Edit3, MoreHorizontal, ArrowUpDown } from 'lucide-react';
import ClientRow from './ClientRow';
import ClientStatusBadge from './ClientStatusBadge';
import ClientResponsible from './ClientResponsible';
import ClientFinancialStatus from './ClientFinancialStatus';
import ClientLastContact from './ClientLastContact';

interface ClientTableProps {
  companies: any[];
  finance: any[];
  onSelect: (company: any) => void;
  onEdit: (company: any, e: React.MouseEvent) => void;
  onViewModal?: (company: any, e?: React.MouseEvent) => void;
  onNavigate?: (company: any, e?: React.MouseEvent) => void;
  selectedCompanyIds: string[];
  toggleSelection: (id: string, e: React.MouseEvent) => void;
  viewMode: 'table' | 'grid';
  sortField?: string;
  onSort?: (field: string) => void;
  sortOrder?: 'asc' | 'desc';
}

export default function ClientTable({
  companies, finance, onSelect, onEdit, onViewModal, onNavigate, selectedCompanyIds, toggleSelection, viewMode, sortField, onSort, sortOrder
}: ClientTableProps) {

  const getInitials = (name: string) => {
    if (!name || typeof name !== 'string') return '?';
    const parts = name.trim().split(/\s+/);
    if (parts.length > 0 && parts[0] !== '') {
      if (parts.length > 1) {
        return (parts[0][0] + parts[1][0]).toUpperCase();
      }
      return parts[0][0].toUpperCase();
    }
    return '?';
  };

  const renderSortIndicator = (field: string) => {
    if (!onSort || sortField !== field) return null;
    return (
      <span className="inline-block ml-1 font-black text-indigo-600">
        {sortOrder === 'asc' ? '↑' : '↓'}
      </span>
    );
  };

  const handleSortClick = (field: string) => {
    if (onSort) onSort(field);
  };

  // 1. Elegant Empty State
  if (companies.length === 0) {
    return (
      <div id="crm-empty-state" className="bg-white border border-[#0F172A0F] rounded-[32px] p-12 shadow-[0_8px_30px_rgb(0,0,0,0.01)] flex flex-col items-center justify-center text-center animate-in fade-in duration-350">
        <div className="w-20 h-20 bg-[#FAFAFA] rounded-full flex items-center justify-center mb-6 border border-[#0F172A0D]">
          <Building2 size={36} className="text-[#64748B]/40" />
        </div>
        <h3 className="text-lg font-bold text-[#111111] mb-1.5 font-display">Nenhum cliente encontrado</h3>
        <p className="text-slate-400 text-xs max-w-sm mb-6 leading-relaxed">Não há registros de clientes que correspondam aos filtros ou termos pesquisados no momento.</p>
        <button 
          onClick={(e) => onEdit(null, e)}
          className="px-5 py-2.5 bg-[#111111] text-white font-bold text-xs rounded-xl hover:bg-black transition-all shadow-3xs cursor-pointer"
        >
          Cadastrar Cliente
        </button>
      </div>
    );
  }

  // 2. Grid View Mode
  if (viewMode === 'grid') {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {companies.map(c => {
          const isChecked = selectedCompanyIds.includes(c.id.toString());
          const handleCardClick = (e: React.MouseEvent) => {
            if (onNavigate) {
              onNavigate(c, e);
            } else {
              onSelect(c);
            }
          };

          return (
            <div 
              key={c.id} 
              onClick={handleCardClick}
              className={`bg-white border rounded-[24px] shadow-3xs hover:shadow-xs transition-all duration-200 cursor-pointer group flex flex-col overflow-hidden relative ${
                isChecked ? 'border-indigo-500/50 ring-2 ring-indigo-500/5' : 'border-[#0F172A0F] hover:border-slate-300'
              }`}
            >
              {/* Card Cover Area */}
              <div className="h-20 w-full bg-slate-100 relative overflow-hidden border-b border-[#0F172A0D]">
                {c.coverUrl ? (
                  <img src={c.coverUrl} alt="Cover" className="w-full h-full object-cover absolute inset-0" />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-slate-100 to-slate-200 absolute inset-0" />
                )}
                {/* Checkbox absolute floating */}
                <div className="absolute top-3 left-3 z-10" onClick={(e) => e.stopPropagation()}>
                  <input 
                    type="checkbox" 
                    checked={isChecked}
                    onChange={(e) => toggleSelection(c.id.toString(), e as any)}
                    className="w-4.5 h-4.5 rounded-lg border-slate-300 text-indigo-600 focus:ring-indigo-500 accent-indigo-600 cursor-pointer shadow-3xs bg-white" 
                  />
                </div>
              </div>

              {/* Profile Image & Upper Controls */}
              <div className="px-5 pb-5 pt-0 flex flex-col flex-1">
                <div className="flex justify-between items-start -mt-6 mb-4 relative z-10">
                  <div className="w-12 h-12 rounded-xl border-2 border-white shadow-3xs overflow-hidden bg-white shrink-0">
                    {c.logoUrl ? (
                      <img 
                        src={c.logoUrl} 
                        alt={c.name} 
                        referrerPolicy="no-referrer"
                        className="w-full h-full object-contain p-1" 
                      />
                    ) : (
                      <div className="w-full h-full bg-[#0F172A] text-white flex items-center justify-center font-bold text-xs">
                        {getInitials(c.name)}
                      </div>
                    )}
                  </div>
                  
                  <div className="flex items-center gap-1.5 mt-7" onClick={(e) => e.stopPropagation()}>
                    <button 
                      type="button"
                      onClick={(e) => { e.stopPropagation(); onViewModal?.(c, e); }}
                      className="p-1.5 rounded-lg bg-slate-50 text-slate-500 hover:bg-slate-100 hover:text-slate-800 transition-all cursor-pointer border border-slate-200/50"
                      title="Visualizar informações"
                    >
                      <Eye size={14} />
                    </button>
                    <button 
                      type="button"
                      onClick={(e) => { e.stopPropagation(); onEdit(c, e); }}
                      className="px-2.5 py-1.5 rounded-lg bg-[#0F172A] text-white text-[10px] font-bold hover:bg-slate-800 transition-all shadow-3xs cursor-pointer flex items-center gap-1"
                      title="Editar cadastro"
                    >
                      <Edit3 size={11} />
                      <span>Editar</span>
                    </button>
                  </div>
                </div>

                {/* Body Details */}
                <div className="flex-1 flex flex-col text-left">
                  <div className="flex items-center justify-between">
                    <h4 className="font-bold text-slate-800 text-base leading-tight group-hover:text-indigo-600 transition-colors">
                      {c.name}
                    </h4>
                    <span className="text-slate-400 group-hover:translate-x-0.5 transition-transform">
                      <ArrowRight size={15} />
                    </span>
                  </div>
                  <p className="text-xs font-semibold text-slate-400 mt-1">{c.industry || 'Tecnologia'}</p>

                  {/* Status Badge */}
                  <div className="mt-3.5 flex flex-wrap gap-2">
                    <ClientStatusBadge status={c.status || 'Ativo'} />
                  </div>

                  {/* Operational indicators */}
                  <div className="mt-5 pt-4 border-t border-slate-100 flex flex-col gap-3 flex-1">
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Responsável</span>
                      <ClientResponsible name={c.responsibleName || 'Equipe Comercial'} role={c.responsibleRole} />
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Contato</span>
                      <ClientLastContact days={c.lastContactDays !== undefined ? c.lastContactDays : 3} />
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Financeiro</span>
                      <ClientFinancialStatus status={c.financialStatus || 'Em dia'} />
                    </div>
                    <div className="flex items-center justify-between pt-1">
                      <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Projetos</span>
                      <span className={`px-2 py-0.5 rounded text-xs font-bold ${
                        c.projects > 0 ? 'text-indigo-600 bg-indigo-50/50' : 'text-slate-400 bg-slate-50'
                      }`}>
                        {c.projects > 0 ? `${c.projects} ativos` : 'Nenhum'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  // 3. Desktop Table View Mode (standard responsive list)
  return (
    <>
      <div className="hidden lg:block bg-white border border-[#0F172A0F] rounded-[24px] shadow-[0_1px_3px_rgba(15,23,42,0.03)] overflow-hidden relative">
        <div className="overflow-x-auto">
          <table className="w-full text-left min-w-[1000px] border-collapse">
            <thead>
              <tr className="bg-slate-50/75 border-b border-[#0F172A0D] text-[10px] font-bold uppercase text-slate-500 tracking-wider">
                <th className="px-5 py-4 w-12 text-center">
                  <input 
                    type="checkbox" 
                    checked={companies.length > 0 && selectedCompanyIds.length === companies.length}
                    onChange={(e) => {
                      const isAllChecked = selectedCompanyIds.length === companies.length;
                      if (isAllChecked) {
                        companies.forEach(c => {
                          if (selectedCompanyIds.includes(c.id.toString())) {
                            toggleSelection(c.id.toString(), e as any);
                          }
                        });
                      } else {
                        companies.forEach(c => {
                          if (!selectedCompanyIds.includes(c.id.toString())) {
                            toggleSelection(c.id.toString(), e as any);
                          }
                        });
                      }
                    }}
                    className="w-4.5 h-4.5 rounded-lg border-slate-300 text-indigo-600 focus:ring-indigo-500 accent-indigo-600 cursor-pointer shadow-3xs bg-white" 
                  />
                </th>
                <th onClick={() => handleSortClick('name')} className="py-4 px-3 font-black cursor-pointer hover:text-slate-800 transition-colors">
                  Cliente {renderSortIndicator('name')}
                </th>
                <th onClick={() => handleSortClick('responsibleName')} className="py-4 px-3 font-black cursor-pointer hover:text-slate-800 transition-colors">
                  Responsável {renderSortIndicator('responsibleName')}
                </th>
                <th onClick={() => handleSortClick('projects')} className="py-4 px-3 font-black cursor-pointer hover:text-slate-800 transition-colors">
                  Projetos {renderSortIndicator('projects')}
                </th>
                <th onClick={() => handleSortClick('lastContactDays')} className="py-4 px-3 font-black cursor-pointer hover:text-slate-800 transition-colors">
                  Último Contato {renderSortIndicator('lastContactDays')}
                </th>
                <th className="py-4 px-3 font-black">Financeiro</th>
                <th onClick={() => handleSortClick('status')} className="py-4 px-3 font-black cursor-pointer hover:text-slate-800 transition-colors">
                  Status {renderSortIndicator('status')}
                </th>
                <th className="px-5 py-4 font-black text-right w-52">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#0F172A0A]">
              {companies.map((c) => (
                <ClientRow 
                  key={c.id}
                  company={c}
                  onSelect={onSelect}
                  onEdit={onEdit}
                  onViewModal={onViewModal}
                  onNavigate={onNavigate}
                  selectedCompanyIds={selectedCompanyIds}
                  toggleSelection={toggleSelection}
                />
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Mobile Card View (fallback shown on touch screens when viewMode is 'table') */}
      <div className="block lg:hidden grid grid-cols-1 sm:grid-cols-2 gap-4">
        {companies.map(c => {
          const isChecked = selectedCompanyIds.includes(c.id.toString());
          const handleMobileClick = (e: React.MouseEvent) => {
            if (onNavigate) {
              onNavigate(c, e);
            } else {
              onSelect(c);
            }
          };

          return (
            <div 
              key={c.id} 
              onClick={handleMobileClick}
              className={`bg-white border rounded-[24px] p-5 shadow-3xs cursor-pointer flex flex-col relative ${
                isChecked ? 'border-indigo-500/50 ring-2 ring-indigo-500/5' : 'border-[#0F172A0D]'
              }`}
            >
              {/* Upper Section */}
              <div className="flex items-start justify-between gap-4 mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl border border-slate-100 flex items-center justify-center shrink-0 overflow-hidden bg-white">
                    {c.logoUrl ? (
                      <img 
                        src={c.logoUrl} 
                        alt={c.name} 
                        referrerPolicy="no-referrer"
                        className="w-full h-full object-contain p-1" 
                      />
                    ) : (
                      <div className="w-full h-full bg-[#0F172A] text-white flex items-center justify-center font-bold text-xs">
                        {getInitials(c.name)}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col text-left">
                    <h4 className="font-bold text-slate-800 text-sm leading-tight">{c.name}</h4>
                    <span className="text-[11px] font-semibold text-slate-400 mt-0.5">{c.industry || 'Tecnologia'}</span>
                  </div>
                </div>

                <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                  <button 
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onViewModal?.(c, e); }}
                    className="p-1.5 rounded-lg bg-slate-50 text-slate-500 hover:bg-slate-100 cursor-pointer border border-slate-200/50"
                  >
                    <Eye size={13} />
                  </button>
                  <button 
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onEdit(c, e); }}
                    className="p-1.5 rounded-lg bg-slate-50 text-slate-500 hover:bg-slate-100 cursor-pointer border border-slate-200/50"
                  >
                    <Edit3 size={13} />
                  </button>
                  <input 
                    type="checkbox" 
                    checked={isChecked}
                    onChange={(e) => toggleSelection(c.id.toString(), e as any)}
                    className="w-4.5 h-4.5 rounded-lg border-slate-300 text-indigo-600 focus:ring-indigo-500 accent-indigo-600 cursor-pointer ml-1" 
                  />
                </div>
              </div>

              {/* Status and Contacts Row */}
              <div className="flex items-center gap-2 mb-4 flex-wrap">
                <ClientStatusBadge status={c.status || 'Ativo'} />
              </div>

              {/* Details List */}
              <div className="flex flex-col gap-2.5 pt-3.5 border-t border-slate-100 mt-auto">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Responsável</span>
                  <span className="text-xs font-bold text-slate-700">{c.responsibleName || 'Equipe Comercial'}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Contato</span>
                  <ClientLastContact days={c.lastContactDays !== undefined ? c.lastContactDays : 3} />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Financeiro</span>
                  <ClientFinancialStatus status={c.financialStatus || 'Em dia'} />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}
